<template>
  <AppDialog :is-open="isDialogOpened"
             :width="step === STEPS_NAMES.CHOSE_FORMULA ? 960 : 800"
             :hide-footer="step === STEPS_NAMES.CHOSE_FORMULA"
             :headerDivider="step === STEPS_NAMES.SETUP_DATA_ROOM || step === STEPS_NAMES.CONTACT_FORM"
             @cancel="closeDialog"
  >
    <template #title>
      <span class="text--large text--extra-bold">{{ $t(`projects.dialogs.ProjectsNewDataroomDialog.dialogTitle.${step}`) }}</span>
    </template>
    <template v-if="step === STEPS_NAMES.SETUP_DATA_ROOM || step === STEPS_NAMES.CONTACT_FORM" #subTitle>
      {{ $t(`projects.dialogs.ProjectsNewDataroomDialog.dialogSubtitle.${step}`) }}
    </template>
    <template #body>
      <ProjectsNewDataroomDialogChooseFormula v-show="step === STEPS_NAMES.CHOSE_FORMULA"
                                              @configureClicked="step = STEPS_NAMES.SETUP_DATA_ROOM"
                                              @requestActivation="onRequestActivation"
      />
      <ProjectsNewDataroomDialogSetupDataRoom v-show="step === STEPS_NAMES.SETUP_DATA_ROOM"
                                              :new-data-room="newDataRoom"
                                              :validator="v$.termsChecked"
                                              @changeOffer="step = STEPS_NAMES.CONTACT_FORM"
      />
      <ProjectsNewDataroomDialogCreateDataRoom v-show="step === STEPS_NAMES.CREATE_DATA_ROOM"
                                               :new-data-room="newDataRoom"
                                               :validator="v$.newDataRoom"
      />
      <ProjectsNewDataroomDialogContactForm
        v-if="step === STEPS_NAMES.CONTACT_FORM"
        ref="contactForm"
        @formSubmitSuccess="onContactFormSubmitSuccess"
      />
    </template>
    <template v-if="step !== STEPS_NAMES.CHOSE_FORMULA" #footer>
      <div class="d-flex justify-space-between w-100">
        <div>
          <app-button
            v-if="step === STEPS_NAMES.CREATE_DATA_ROOM && !canCreateDataroomUnlimited"
            type="outlined"
            variant="neutral"
            :disabled="postRoomProcessing"
            @click="step = STEPS_NAMES.SETUP_DATA_ROOM"
          >
            {{ $t('common.previous') }}
          </app-button>
        </div>
        <div class="d-flex ga-4">
          <app-button
            type="outlined"
            variant="neutral"
            :disabled="postRoomProcessing || contactFormRequestPending"
            @click="onCancelClick"
          >
            {{ $t('common.cancel') }}
          </app-button>
          <app-button
            :loading="postRoomProcessing || contactFormRequestPending"
            @click="onOkClick"
          >
            {{ okText }}
          </app-button>
        </div>
      </div>
    </template>
  </AppDialog>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { maxLength, required, sameAs } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDialog from '@/common/dialogs/AppDialog.vue'
import { track } from '@/common/pendo/agent'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { DataRoomToPost } from '@/models/room.model'

import ProjectsNewDataroomDialogChooseFormula from './ProjectsNewDataroomDialogChooseFormula.vue'
import ProjectsNewDataroomDialogContactForm from './ProjectsNewDataroomDialogContactForm.vue'
import ProjectsNewDataroomDialogCreateDataRoom from './ProjectsNewDataroomDialogCreateDataRoom.vue'
import ProjectsNewDataroomDialogSetupDataRoom from './ProjectsNewDataroomDialogSetupDataRoom.vue'

const STEPS_NAMES = Object.freeze({
  CHOSE_FORMULA: 'CHOSE_FORMULA',
  SETUP_DATA_ROOM: 'SETUP_DATA_ROOM',
  CREATE_DATA_ROOM: 'CREATE_DATA_ROOM',
  CONTACT_FORM: 'CONTACT_FORM',
})

const PPU_FLOW_STEP_EVENTS = {
  [STEPS_NAMES.SETUP_DATA_ROOM]: 'pricing',
  [STEPS_NAMES.CREATE_DATA_ROOM]: 'project_creation',
}

export default {
  name: 'ProjectsNewDataroomDialog',
  components: {
    ProjectsNewDataroomDialogChooseFormula,
    ProjectsNewDataroomDialogSetupDataRoom,
    ProjectsNewDataroomDialogCreateDataRoom,
    ProjectsNewDataroomDialogContactForm,
    AppDialog,
  },
  mixins: [dialogMixin],
  setup: () => ({ v$: useVuelidate() }),
  props: {
    isOpenedFromHome: {
      type: Boolean,
      default: true,
    },
  },
  validations: {
    newDataRoom: {
      name: {
        required,
        maxLength: maxLength(100),
      },
    },
    termsChecked: {
      sameAs: sameAs(true),
    },
  },
  data () {
    return {
      STEPS_NAMES,
      step: STEPS_NAMES.CHOSE_FORMULA,
      newDataRoom: new DataRoomToPost(),
      termsChecked: false,
      contactFormRequestPending: false,
    }
  },
  watch: {
    step (newStep) {
      // We only track PPU DR events
      if (this.canCreateDataroomUnlimited) {
        return
      }

      const stepEvent = PPU_FLOW_STEP_EVENTS[newStep]
      if (!stepEvent) {
        return
      }

      const entryPoint = this.isOpenedFromHome ? 'home' : 'discovery_modal'

      track('ppu_flow_step_viewed', {
        step: stepEvent,
        entry_point: entryPoint,
      })
    },
  },
  computed: {
    ...mapGetters('rooms', ['dataRoomTypeId']),
    ...mapState('room', ['postRoomProcessing']),
    ...mapGetters('user', ['canCreateDataroomUnlimited']),
    okText () {
      if (this.step === STEPS_NAMES.SETUP_DATA_ROOM) {
        return this.$t('common.next')
      }

      if (this.step === STEPS_NAMES.CONTACT_FORM) {
        return this.$t('projects.dialogs.ProjectsNewDataroomDialogContactForm.sendRequest')
      }

      return this.$t('common.validate')
    },
  },
  created () {
    if (this.canCreateDataroomUnlimited) {
      this.step = STEPS_NAMES.CREATE_DATA_ROOM
    }
  },
  methods: {
    ...mapActions('room', ['postRoom']),
    async onOkClick () {
      if (this.step === STEPS_NAMES.CONTACT_FORM) {
        const isContactFormValid = await this.$refs.contactForm.sendRequest()
        if (isContactFormValid) {
          this.contactFormRequestPending = true
        }
        return
      }

      if (this.step === STEPS_NAMES.SETUP_DATA_ROOM) {
        this.v$.termsChecked.$touch()
        if (!this.v$.termsChecked.$invalid) {
          this.step = STEPS_NAMES.CREATE_DATA_ROOM
        }
      } else if (this.step === STEPS_NAMES.CREATE_DATA_ROOM) {
        this.v$.newDataRoom.$touch()
        if (!this.v$.newDataRoom.$invalid) {
          try {
            if (!this.newDataRoom.type) {
              this.newDataRoom.type = this.dataRoomTypeId
            }
            await this.postRoom({
              room : this.newDataRoom,
              isEntryPointHome: this.isOpenedFromHome,
              isDrPpu: !this.canCreateDataroomUnlimited,
            })
            toast.success(this.$t('projects.dialogs.ProjectsNewDataroomDialog.postDataRoomSuccess'))
          } catch (error) {
            console.error(error)
            toast.error(this.$t('projects.dialogs.ProjectsNewDataroomDialog.postDataRoomError'))
          }
        }
      }
    },
    onCancelClick () {
      if (this.step === STEPS_NAMES.CONTACT_FORM) {
        this.step = STEPS_NAMES.SETUP_DATA_ROOM
        return
      }

      this.closeDialog()
    },
    onContactFormSubmitSuccess () {
      this.contactFormRequestPending = false
      toast.success(this.$t('projects.dialogs.ProjectTypeQuoteRequestDialog.quoteRequestSuccessMessage'))
      this.closeDialog()
    },
    onRequestActivation () {
      this.closeDialog()
      this.$emit('requestActivation')
    },
  },
}
</script>
